import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@store/types';
import { AirflowDashboardHandler } from '@api/airflow-dashboard-handler';
import { isDevelopment } from '@constants/settings';
import { AirflowImportError } from '../types';

/*
 * Fetches DAG import/parse errors and manages the expanded/collapsed state of the banner
 * shown at the top of the Airflow dashboard
 */
export function useImportErrorsBanner() {
    const { envVariables: Env } = useSelector((store: RootState) => store.AccountReducer);

    /*
     * Airflow requests build their host from Env.REACT_APP_AIRFLOW_URL, which is only
     * populated once the app's post-login env-variables fetch resolves — same guard used by
     * the DAGs list (use-dags-tab.ts) to avoid hitting a malformed "undefined/api/v1" URL.
     */
    const isAirflowConfigReady = isDevelopment || Boolean(Env?.REACT_APP_AIRFLOW_URL);

    const [importErrors, setImportErrors] = useState<AirflowImportError[]>([]);
    const [isExpanded, setIsExpanded] = useState(false);

    /*
     * Fetches the current list of DAG import/parse errors
     */
    const fetchImportErrors = useCallback(async () => {
        if (!isAirflowConfigReady) return;
        try {
            const response = await AirflowDashboardHandler.listImportErrors(Env, { limit: 100 });
            setImportErrors(response.import_errors);
        } catch {
            setImportErrors([]);
        }
    }, [Env, isAirflowConfigReady]);

    useEffect(() => {
        fetchImportErrors();
    }, [fetchImportErrors]);

    /*
     * Toggles the expanded/collapsed state of the traceback list
     */
    const handleToggleExpanded = useCallback(() => setIsExpanded((prev) => !prev), []);

    return {
        importErrors,
        isExpanded,
        handleToggleExpanded,
    };
}
